import { dayjs, parseIsoToTz } from '../lib/dayjs';
import { getItem, setItem } from '../lib/storage';

// Lazy-load date-holidays data on demand to avoid bundling its ~600KB dataset in initial JS.
// We keep a small synchronous fallback (Sunday-only) so existing callers remain sync-safe.

let currentRegion: string | undefined = undefined;

const CACHE_KEY_PREFIX = 'holidays:'; // localforage key prefix (wsc: prefix added by storage wrapper)

// in-memory cached set of YYYY-MM-DD strings per regionKey
const inMemoryCache: Record<string, Set<string>> = {};

function regionKey(region?: string) {
  return region ? region : 'NAT';
}

// (DEV helpers removed) — kept debugging artifacts were intentionally deleted to avoid globals

export function setSpainRegion(region?: string) {
  currentRegion = region;
  // Kick off async preload but do not block callers
  ensureHolidaysLoaded(region).catch(() => {
    /* ignore preload errors */
  });
}

export function getCurrentRegion(): string | undefined {
  return currentRegion;
}

// Ensure the holiday dates for `region` are loaded into in-memory cache.
// Loads from localforage if present; otherwise dynamically imports `date-holidays`
// and extracts the holiday dates for the window of years [currentYear-1, currentYear+1].
export async function ensureHolidaysLoaded(region?: string): Promise<void> {
  if (typeof window === 'undefined') {
    return; // only client-side
  }
  const rk = regionKey(region);
  if (inMemoryCache[rk] && inMemoryCache[rk].size > 0) {
    return;
  }

  const cacheKey = `${CACHE_KEY_PREFIX}${rk}`;
  try {
    const stored = await getItem<{ years: number[]; dates: string[] }>(cacheKey);
    const currentYear = dayjs().year();
    if (stored && Array.isArray(stored.years) && stored.years.includes(currentYear)) {
      inMemoryCache[rk] = new Set(stored.dates || []);
      return;
    }
  } catch (e) {
    // ignore and proceed to fetch
  }

  try {
    // dynamic import to keep package out of initial bundle
    const { default: Holidays } = await import('date-holidays');
    const hd = region ? new Holidays('ES', region) : new Holidays('ES');
    const years = [dayjs().year() - 1, dayjs().year(), dayjs().year() + 1];
    const dates: string[] = [];
    for (const y of years) {
      // hd.getHolidays(year) returns objects with date strings
      // we normalize to YYYY-MM-DD in Europe/Madrid local date
      const hy = hd.getHolidays(y) || [];
      for (const h of hy) {
        try {
          const d = dayjs(h.date).tz();
          dates.push(d.format('YYYY-MM-DD'));
        } catch (err) {
          // fallback: try to parse with dayjs
          const dd = dayjs(h.date);
          if (dd.isValid()) {
            const iso = dd.tz().format('YYYY-MM-DD');
            dates.push(iso);
          }
        }
      }
    }
    inMemoryCache[rk] = new Set(dates);
    // persist minimal cache for faster subsequent loads
    try {
      await setItem(cacheKey, { years, dates });
    } catch (e) {
      // ignore storage write errors
    }
  } catch (e) {
    // dynamic import failed; keep empty cache (fallback to Sunday-only)
    inMemoryCache[rk] = new Set();
  }
}

export function isSpainHoliday(date: Date): boolean {
  // synchronous check: use in-memory cache if available, otherwise fallback to Sunday-only
  const rk = regionKey(currentRegion);
  const cache = inMemoryCache[rk];
  const key = dayjs(date).tz().format('YYYY-MM-DD');
  if (cache && cache.size > 0) {
    return cache.has(key);
  }
  // fallback: only Sundays are considered holidays until async load completes
  return dayjs(date).day() === 0;
}

export async function isSpainHolidayAsync(date: Date, region?: string): Promise<boolean> {
  const rk = regionKey(region ?? currentRegion);
  await ensureHolidaysLoaded(region ?? currentRegion);
  const cache = inMemoryCache[rk];
  const key = dayjs(date).tz().format('YYYY-MM-DD');
  return !!(cache && cache.has(key));
}

export function isHolidayOrSunday(date: Date | string): boolean {
  const d = typeof date === 'string' ? parseIsoToTz(date).toDate() : date;
  const isSunday = dayjs(d).day() === 0;
  return isSunday || isSpainHoliday(d);
}

export { isHolidayOrSunday as isHolidayOrSundaySync, isSpainHoliday as isSpainHolidaySync };

export function getSpainRegionOptions(): Array<{ value: string; label: string }> {
  // Return a canonical list of Spain autonomous communities and cities
  // so the UI always shows the complete dropdown regardless of date-holidays data.
  const canonical = [
    // { value: '', label: 'Nacional' },
    // { value: 'AN', label: 'Andalucía (AN)' },
    { value: 'AR', label: 'Aragón (AR)' },
    // { value: 'AS', label: 'Asturias (AS)' },
    // { value: 'CB', label: 'Cantabria (CB)' },
    // { value: 'CL', label: 'Castilla y León (CL)' },
    // { value: 'CM', label: 'Castilla-La Mancha (CM)' },
    // { value: 'CN', label: 'Canarias (CN)' },
    // { value: 'CT', label: 'Cataluña (CT)' },
    // { value: 'VC', label: 'Comunidad Valenciana (VC)' },
    // { value: 'EX', label: 'Extremadura (EX)' },
    { value: 'GA', label: 'Galicia (GA)' },
    // { value: 'IB', label: 'Islas Baleares (IB)' },
    // { value: 'MC', label: 'Región de Murcia (MC)' },
    // { value: 'MD', label: 'Comunidad de Madrid (MD)' },
    // { value: 'NC', label: 'Navarra (NC)' },
    // { value: 'PV', label: 'País Vasco (PV)' },
    // { value: 'RI', label: 'La Rioja (RI)' },
    // { value: 'CE', label: 'Ceuta (CE)' },
    // { value: 'ML', label: 'Melilla (ML)' },
  ];
  return canonical;
}
